import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  flattenInspectionChecklistRows,
  InspectionVirtualRow,
  TreeNode,
} from 'shared/domain/checklistTree';

export const SECTION_ROW_TRANSITION_MS = 200;

export type SectionRowPhase = 'enter' | 'exit';

const EMPTY_IDS: ReadonlySet<string> = new Set();

/**
 * Flat rows have no accordion `Collapse` around a section's children, so the
 * list animates them itself: rows inserted by expanding a section `enter`, and
 * rows of a collapsed section stay rendered as `exit` until the animation ends.
 * Rows mounted by scrolling are not new rows and never animate.
 */
export function useSectionRowTransitions(
  checklistTree: TreeNode[],
  expandedIds: Set<string>
): {
  rows: InspectionVirtualRow[];
  getRowPhase: (row: InspectionVirtualRow) => SectionRowPhase | null;
  /** Expanded, or still showing its children while they animate out. */
  isSectionFrameOpen: (sectionId: string) => boolean;
} {
  const [collapsingIds, setCollapsingIds] =
    useState<ReadonlySet<string>>(EMPTY_IDS);
  const [enteringKeys, setEnteringKeys] =
    useState<ReadonlySet<string>>(EMPTY_IDS);

  // Derived during render so collapsed rows never leave the list for a commit
  // (that would remount their editors mid-animation).
  const [previousExpandedIds, setPreviousExpandedIds] = useState(expandedIds);
  if (previousExpandedIds !== expandedIds) {
    setPreviousExpandedIds(expandedIds);
    const next = new Set(
      [...collapsingIds].filter((id) => !expandedIds.has(id))
    );
    previousExpandedIds.forEach((id) => {
      if (!expandedIds.has(id)) {
        next.add(id);
      }
    });
    if (next.size > 0 || collapsingIds.size > 0) {
      setCollapsingIds(next);
    }
  }

  const collapseTimeoutsRef = useRef(new Map<string, number>());
  useEffect(() => {
    const timeouts = collapseTimeoutsRef.current;
    timeouts.forEach((timeoutId, id) => {
      if (!collapsingIds.has(id)) {
        window.clearTimeout(timeoutId);
        timeouts.delete(id);
      }
    });
    collapsingIds.forEach((id) => {
      if (timeouts.has(id)) {
        return;
      }
      timeouts.set(
        id,
        window.setTimeout(() => {
          timeouts.delete(id);
          setCollapsingIds((current) => {
            const next = new Set(current);
            next.delete(id);
            return next;
          });
        }, SECTION_ROW_TRANSITION_MS)
      );
    });
  }, [collapsingIds]);

  const enterTimeoutsRef = useRef(new Set<number>());
  useEffect(() => {
    const collapseTimeouts = collapseTimeoutsRef.current;
    const enterTimeouts = enterTimeoutsRef.current;
    return () => {
      collapseTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      collapseTimeouts.clear();
      enterTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      enterTimeouts.clear();
    };
  }, []);

  const renderedExpandedIds = useMemo(() => {
    if (collapsingIds.size === 0) {
      return expandedIds;
    }
    const ids = new Set(expandedIds);
    collapsingIds.forEach((id) => ids.add(id));
    return ids;
  }, [collapsingIds, expandedIds]);

  const rows = useMemo(
    () => flattenInspectionChecklistRows(checklistTree, renderedExpandedIds),
    [checklistTree, renderedExpandedIds]
  );

  const previousRowsRef = useRef<{
    keys: ReadonlySet<string>;
    expandedIds: Set<string>;
  } | null>(null);
  useLayoutEffect(() => {
    const previous = previousRowsRef.current;
    const keys = new Set(rows.map((row) => row.key));
    previousRowsRef.current = { keys, expandedIds };
    // First render, or rows changed for another reason (data refresh).
    if (!previous || previous.expandedIds === expandedIds) {
      return;
    }

    const added = rows
      .map((row) => row.key)
      .filter((key) => !previous.keys.has(key));
    if (added.length === 0) {
      return;
    }
    setEnteringKeys((current) => new Set([...current, ...added]));
    const timeoutId = window.setTimeout(() => {
      enterTimeoutsRef.current.delete(timeoutId);
      setEnteringKeys((current) => {
        const next = new Set(current);
        added.forEach((key) => next.delete(key));
        return next;
      });
    }, SECTION_ROW_TRANSITION_MS);
    enterTimeoutsRef.current.add(timeoutId);
    // Only a new `rows` array can add keys; `expandedIds` is compared via the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const getRowPhase = useMemo(
    () =>
      (row: InspectionVirtualRow): SectionRowPhase | null => {
        if (
          collapsingIds.size > 0 &&
          row.frames.some((frame) => collapsingIds.has(frame.sectionId))
        ) {
          return 'exit';
        }
        return enteringKeys.has(row.key) ? 'enter' : null;
      },
    [collapsingIds, enteringKeys]
  );

  const isSectionFrameOpen = useCallback(
    (sectionId: string) => renderedExpandedIds.has(sectionId),
    [renderedExpandedIds]
  );

  return { rows, getRowPhase, isSectionFrameOpen };
}
